import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Heart, MessageCircle } from "lucide-react";
import { getContent } from "../lib/api";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { useLang } from "../context/LanguageContext";
import { StoryViewer } from "../components/media/StoryViewer";
import { SmartImage } from "../components/SmartImage";
import { SocialLinks } from "../components/SocialLinks";

const ALL = "__all__";

export default function Media() {
  const { t, tc, lang } = useLang();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(ALL);
  const [story, setStory] = useState(null);

  useEffect(() => {
    Promise.all([getContent("media"), getContent("media-categories")])
      .then(([media, cats]) => { setItems(media); setCategories(cats); })
      .catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const map = {};
    items.forEach((i) => { map[i.category] = (map[i.category] || 0) + 1; });
    return map;
  }, [items]);

  const visibleCats = categories.filter((c) => counts[c.slug]);
  const filtered = active === ALL ? items : items.filter((i) => i.category === active);
  const catName = (slug) => tc(categories.find((c) => c.slug === slug)?.name) || slug;

  return (
    <>
      <SEO pageKey="media" title={t.media.seoTitle} description={t.media.seoDescription} />
      <PageHeader compact overline={t.media.overline} title={t.media.title} sub={t.media.sub} />

      <section className="bg-white border-b border-border" data-testid="media-stories">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 flex items-start gap-6 sm:gap-9">
          <div className="flex gap-6 sm:gap-9 overflow-x-auto no-scrollbar flex-1">
          {visibleCats.map((c) => (
            <button
              key={c.slug}
              onClick={() => setStory(c.slug)}
              data-testid={`media-story-${c.slug}`}
              className="shrink-0 flex flex-col items-center gap-2 group"
            >
              <span className={`relative w-[76px] h-[76px] sm:w-[92px] sm:h-[92px] rounded-full p-[3px] transition-transform duration-300 group-hover:scale-105 ${story === c.slug ? "bg-gold" : "bg-gradient-to-tr from-green via-green-light to-gold"}`}>
                <span className="block w-full h-full rounded-full p-[2px] bg-white">
                  {c.cover_image || items.find((i) => i.category === c.slug)?.image ? (
                    <img
                      src={c.cover_image || items.find((i) => i.category === c.slug)?.image}
                      alt={catName(c.slug)}
                      className="w-full h-full rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="w-full h-full rounded-full bg-charcoal block" />
                  )}
                </span>
              </span>
              <span className="text-charcoal text-xs sm:text-sm font-medium leading-none">{catName(c.slug)}</span>
              <span className="text-charcoal/45 text-[11px] leading-none">{counts[c.slug]}</span>
            </button>
          ))}
          </div>
          <SocialLinks theme="light" place="media" className="hidden lg:flex pt-6 shrink-0" />
        </div>
      </section>

      <section className="bg-white sticky top-[76px] sm:top-[92px] z-30 border-b border-border" data-testid="media-filters">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          <FilterPill active={active === ALL} onClick={() => setActive(ALL)} testid="media-filter-all">{t.media.all}</FilterPill>
          {visibleCats.map((c) => (
            <FilterPill key={c.slug} active={active === c.slug} onClick={() => setActive(c.slug)} testid={`media-filter-${c.slug}`}>
              {catName(c.slug)}
            </FilterPill>
          ))}
        </div>
      </section>

      <section className="bg-lightgray py-10 sm:py-14">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-9" data-testid="media-grid">
            {filtered.map((item) => (
              <Link
                key={item.slug}
                to={`/media/${item.slug}`}
                data-testid={`media-card-${item.slug}`}
                className="group block"
              >
                <div className="relative aspect-video md:aspect-square overflow-hidden bg-charcoal">
                  {item.image ? (
                    <SmartImage
                      src={item.image}
                      variants={item.image_variants}
                      alt={tc(item.title)}
                      fit="cover"
                      className="w-full h-full"
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                      imgClassName="transition-transform duration-700 group-hover:scale-[1.06]"
                    />
                  ) : (
                    <span className="absolute inset-0 grain flex items-center justify-center bg-gradient-to-br from-green to-charcoal">
                      <span className="font-display text-3xl text-gold/70">{(tc(item.title) || "S").trim().charAt(0)}</span>
                    </span>
                  )}
                  {item.video_url && (
                    <span className="absolute top-2.5 end-2.5 w-7 h-7 rounded-full bg-charcoal/70 backdrop-blur flex items-center justify-center">
                      <Play className="w-3.5 h-3.5 text-white fill-white" />
                    </span>
                  )}
                  <span className="absolute bottom-2.5 start-2.5 flex items-center gap-2.5 text-white text-[11px] drop-shadow sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {item.likes || 0}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {item.comment_count || 0}</span>
                  </span>
                </div>
                <span className="block mt-3 text-[10px] uppercase tracking-[0.18em] text-gold font-semibold">{catName(item.category)}</span>
                <h3 className="mt-1.5 font-display text-[15px] sm:text-lg leading-snug text-charcoal group-hover:text-green-light transition-colors line-clamp-2">
                  {tc(item.title)}
                </h3>
              </Link>
            ))}
          </div>
          {!filtered.length && (
            <p className="text-charcoal/45 text-center py-16" data-testid="media-empty">{t.media.empty}</p>
          )}
        </div>
      </section>

      {story && (
        <StoryViewer
          items={items.filter((i) => i.category === story)}
          categoryName={catName(story)}
          onClose={() => setStory(null)}
        />
      )}
    </>
  );
}

const FilterPill = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`shrink-0 rounded-full px-4 py-2 text-[11px] sm:text-xs uppercase tracking-[0.14em] font-semibold transition-colors ${
      active ? "bg-green text-white" : "text-charcoal/55 hover:text-charcoal hover:bg-lightgray"
    }`}
  >
    {children}
  </button>
);
